import type { ReactElement } from "react";

import { HeaderLanguageSwitcherProvider } from "@/components/Header/HeaderLanguageSwitcherContext";
import MainLayout from "@/components/Layouts/MainLayout";
import {
  BrandStoryPage,
  type BrandStoryPageProps,
} from "@/features/brand-story/BrandStoryPage";
import type { ListingPageContent } from "@/features/search/api/listing-page-content";
import type { RelatedPage } from "@/features/search/api/related-pages";
import type { ListingPageMeta, PageInfo } from "@/features/search/api/types";
import { SearchLayout } from "@/features/search/components/SearchLayout";
import { SearchProvider } from "@/features/search/components/SearchProvider";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";
import { SearchRobots } from "@/features/search/components/seo/SearchRobots";
import { SearchSeo } from "@/features/search/components/seo/SearchSeo";
import type { PageRuleTerms } from "@/features/search/utils/index-policy.utils";
import type { ListingHeadFacts } from "@/features/search/utils/listing-head.utils";
import { toLocalizedPaths } from "@/features/search/utils/url.utils";

/**
 * The site's search page (WHO-5064) — the rendering half; its `getServerSideProps` is in
 * `SearchPage.server.ts`.
 *
 * The two halves live apart because a page module is compiled for the browser too: importing the
 * loader at the top of `pages/[...search].tsx` would drag `next-i18next`'s filesystem reader and
 * Apollo's server client into the client bundle. The page imports this component statically and the
 * loader dynamically, inside `getServerSideProps`, which Next strips from the client build.
 *
 * It serves query searches (`/search?query=chair`) and curated landing pages
 * (`/furniture/chairs-and-lounge-chairs/dining-chairs`) from one route — see `api/route.ts` for how
 * a URL maps to an endpoint. Everything below `SearchProvider` is identical for both.
 */

export type SearchRouteProps = {
  /** Landing-page SEO metadata; `null` for a plain query search. */
  pageMeta: ListingPageMeta;
  /** The page's editorial copy, from Strapi; `null` when it has none. */
  content: ListingPageContent | null;
  /** Links to sibling landing pages, from the gateway; empty when there are none. */
  relatedPages: RelatedPage[];
  /** The page's newest listings, for the "New this week" module; empty when it does not apply. */
  newThisWeek: ProductCardProduct[];
  /**
   * The server-rendered result count, for the robots directive; `null` when the search failed.
   * Only `total` is carried: the full `pageInfo` already reaches the client in the dehydrated
   * query cache, and this stays in step with the HTML the crawler is given.
   */
  pageInfo: Pick<PageInfo, "total"> | null;
  /**
   * The term rules the page ran with, for the publish floor and the facet whitelist (see
   * `utils/index-policy.utils.ts`); `null` when the search failed. Only the terms are carried, for
   * the same reason only `total` is.
   */
  pageRules: PageRuleTerms | null;
  /**
   * WHO-6325: the server judged the page too thin to index (`utils/thin-page.utils.ts`); only ever
   * `true` while the `seo_thin_page_rule` gate is on.
   */
  thinListing?: boolean;
  /**
   * WHO-5684: the server fetched the results without the viewer so the HTML could be cached at
   * the CDN; a signed-in client refetches once with its token to get its hearts.
   */
  anonymousHtml?: boolean;
  /**
   * WHO-5756: the count, brands and first listings the head title, description and ItemList are
   * built from when Strapi has no SEO copy; `null` when the search failed.
   */
  headFacts?: ListingHeadFacts | null;
  /** Landing-page path without locale prefix, the editorial-links key; `null` for a query search. */
  pagePath?: string | null;
  /** WHO-5760: the live-data intro sentence for pages without Strapi copy. */
  generatedIntro?: string | null;
  /**
   * An English brand story for a brand the catalog has no landing page for yet; when set, it is
   * the whole page and every other prop is absent.
   */
  brandStory?: BrandStoryPageProps;
};

export const SearchPage = (props: SearchRouteProps) =>
  props.brandStory ? (
    <BrandStoryPage {...props.brandStory} />
  ) : (
    <ListingSearchPage {...props} />
  );

const ListingSearchPage = ({
  pageMeta,
  content,
  relatedPages,
  newThisWeek,
  pageInfo,
  pageRules,
  thinListing = false,
  anonymousHtml,
  headFacts = null,
  pagePath = null,
  generatedIntro = null,
}: SearchRouteProps) => (
  <>
    <SearchSeo page={pageMeta} content={content} headFacts={headFacts} />
    <SearchRobots
      pageInfo={pageInfo}
      pageRules={pageRules}
      pageUrl={pageMeta?.url}
      thinListing={thinListing}
    />
    <SearchProvider hydratedAnonymously={anonymousHtml}>
      <SearchLayout
        content={content}
        relatedPages={relatedPages}
        newThisWeek={newThisWeek}
        pagePath={pagePath}
        generatedIntro={generatedIntro}
      />
    </SearchProvider>
  </>
);

/*
 * The language switcher sits in the header, above the page — and a landing page's slug
 * differs per locale ("/furniture" vs "/nl/meubels"), so it needs the service's
 * alternates or switching language 404s.
 */
SearchPage.getLayout = (page: ReactElement, props: SearchRouteProps) => (
  <HeaderLanguageSwitcherProvider
    value={{
      urls: props.brandStory
        ? { en: props.brandStory.path }
        : toLocalizedPaths(props.pageMeta?.alternateUrls),
    }}
  >
    <MainLayout>{page}</MainLayout>
  </HeaderLanguageSwitcherProvider>
);
